'use client';

import { CheckCircle2, X, Loader2, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ChatJobStatus } from '@/types/chat';

interface ConfirmUploadBannerProps {
  onConfirm: () => void;
  onCancel: () => void;
  isProcessing: boolean;
  jobStatus: ChatJobStatus | null;
}

/**
 * Banner shown when a file has been previewed and is waiting
 * for the user to confirm upload and trigger the pipeline.
 */
export function ConfirmUploadBanner({ onConfirm, onCancel, isProcessing, jobStatus }: ConfirmUploadBannerProps) {
  // Show progress if pipeline is running
  if (isProcessing && jobStatus?.status === 'processing') {
    const stage = jobStatus.progress?.stage ?? 'processing';
    const percent = jobStatus.progress?.percent ?? 0;
    const stageLabel = stage.replace(/_/g, ' ');

    return (
      <div className="border-t border-gray-200 bg-blue-50 px-4 py-3">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-blue-800 capitalize">{stageLabel}...</p>
              <div className="mt-1.5 h-1.5 bg-blue-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-500"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
            <span className="text-xs text-blue-600 font-medium flex-shrink-0">{percent}%</span>
          </div>
        </div>
      </div>
    );
  }

  // Show confirmation prompt
  return (
    <div className="border-t border-gray-200 bg-green-50 px-4 py-3">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-green-800">File analyzed and ready for processing</p>
            <p className="text-xs text-green-700 mt-0.5 flex items-center gap-1">
              <MessageCircle className="w-3 h-3" />
              You can continue chatting about the data before confirming.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={onCancel} className="text-gray-500">
              <X className="w-4 h-4 mr-1" />
              Dismiss
            </Button>
            <Button variant="coral" size="sm" onClick={onConfirm} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Processing...
                </>
              ) : (
                'Run Benchmarking Pipeline'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
